import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  BarChart3,
  TrendingUp,
  Users,
  Settings,
  Gamepad2,
  Heart,
  RotateCcw,
  Megaphone,
  Loader2,
} from 'lucide-react';
import { TopBanner } from '@/components/TopBanner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useMetrics, useFunnel } from '@/hooks/useMetrics';
import type { MetricsTab, MetricsCard, MetricsChartItem, FunnelStage } from '@/api/metrics.api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';

export const Route = createFileRoute('/_authenticated/metricas')({
  component: MetricasPage,
});

const TABS: { key: MetricsTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'finanzas', label: 'Finanzas', icon: TrendingUp },
  { key: 'jugadores', label: 'Jugadores', icon: Users },
  { key: 'operativas', label: 'Operativas', icon: Settings },
  { key: 'juegos', label: 'Juegos', icon: Gamepad2 },
  { key: 'engagement', label: 'Engagement', icon: Heart },
  { key: 'retencion', label: 'Retención', icon: RotateCcw },
  { key: 'marketing', label: 'Marketing', icon: Megaphone },
];

const COLORS = ['#d4a843', '#22c55e', '#3b82f6', '#ef4444', '#a855f7', '#f97316'];

type RangeKey = 'hoy' | 'ayer' | '7d' | 'mes' | 'mes_pasado' | 'todo';

const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'hoy', label: 'Hoy' },
  { key: 'ayer', label: 'Ayer' },
  { key: '7d', label: '7 días' },
  { key: 'mes', label: 'Este mes' },
  { key: 'mes_pasado', label: 'Mes pasado' },
  { key: 'todo', label: 'Todo' },
];

/** Convierte un preset en {fromDate, toDate} ISO (undefined = sin filtro = todo). */
function computeRange(key: RangeKey): { fromDate?: string; toDate?: string } {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  switch (key) {
    case 'hoy':
      return { fromDate: startOfDay(now).toISOString(), toDate: now.toISOString() };
    case 'ayer': {
      const y = startOfDay(new Date(now.getTime() - 86400000));
      return { fromDate: y.toISOString(), toDate: startOfDay(now).toISOString() };
    }
    case '7d':
      return { fromDate: new Date(now.getTime() - 7 * 86400000).toISOString(), toDate: now.toISOString() };
    case 'mes':
      return { fromDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), toDate: now.toISOString() };
    case 'mes_pasado':
      return {
        fromDate: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
        toDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      };
    case 'todo':
    default:
      return {};
  }
}

function MetricasPage() {
  const [activeTab, setActiveTab] = useState<MetricsTab>('finanzas');
  const [range, setRange] = useState<RangeKey>('7d');
  const { fromDate, toDate } = computeRange(range);

  return (
    <>
      <TopBanner title="Métricas" subtitle="Finanzas, jugadores, operativas" />
      <div className="flex-1 overflow-auto p-4 sm:p-6 space-y-6">
        {/* Rango de fechas */}
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                range === r.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background border hover:bg-accent'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === t.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background border hover:bg-accent'
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <MetricsTabContent tab={activeTab} fromDate={fromDate} toDate={toDate} />

        {/* Funnel */}
        <FunnelSection />
      </div>
    </>
  );
}

function MetricsTabContent({
  tab,
  fromDate,
  toDate,
}: {
  tab: MetricsTab;
  fromDate?: string;
  toDate?: string;
}) {
  const { data, isLoading } = useMetrics(tab, fromDate, toDate);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const cards = data?.cards ?? [];
  const chart = data?.chart ?? [];

  return (
    <div className="space-y-6">
      {/* Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card: MetricsCard, i: number) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{card.label}</div>
              <div className="mt-2 text-2xl font-semibold">
                {typeof card.value === 'number' ? card.value.toLocaleString('es-AR') : card.value}
                {card.suffix && <span className="text-sm ml-1">{card.suffix}</span>}
              </div>
              {card.change && (
                <Badge variant={card.change.startsWith('+') ? 'success' : 'secondary'} className="mt-2">
                  {card.change}
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {TABS.find((t) => t.key === tab)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value: number) => value.toLocaleString('es-AR')} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chart.map((_: MetricsChartItem, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FunnelSection() {
  const { data, isLoading } = useFunnel();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const stages = data?.stages ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Funnel de Conversión
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {stages.map((stage: FunnelStage, i: number) => (
            <div key={stage.name} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{stage.name}</span>
                <span className="text-muted-foreground">
                  {stage.value.toLocaleString('es-AR')} ({stage.percent}%)
                </span>
              </div>
              <div className="h-8 w-full rounded-md bg-secondary overflow-hidden">
                <div
                  className="h-full flex items-center justify-end px-2 text-xs font-medium text-primary-foreground transition-all"
                  style={{
                    width: `${Math.max(stage.percent, 5)}%`,
                    backgroundColor: COLORS[i % COLORS.length],
                  }}
                >
                  {stage.percent > 15 && `${stage.percent}%`}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
